import { useApolloClient } from '@apollo/client';
import {
	BoardFragmentFragment,
	type UpdateBoardMutationMutationVariables,
} from '@apps/www/src/__generated__/graphql';
import SVEditBoardCollaboratorsActionContainer from '@apps/www/src/www/containers/SVEditBoardCollaboratorsActionContainer';
import SVInviteUsersContainer from '@apps/www/src/www/containers/SVInviteUsersContainer';
import useAuthUser from '@apps/www/src/www/hooks/useAuthUser';
import SVDeleteBoardModal from '@apps/www/src/www/modals/SVDeleteBoardModal';
import updateBoardMutation from '@apps/www/src/www/queries/updateBoardMutation';
import UserItemsQuery from '@apps/www/src/www/queries/UserItemsQuery';
import SVAvatarsStack from '@pkgs/shared-client/components/SVAvatarsStack';
import SVButton, { SVButtonSIZES, SVButtonUSES } from '@pkgs/shared-client/components/SVButton';
import SVFormLayout from '@pkgs/shared-client/components/SVFormLayout';
import SVMessage from '@pkgs/shared-client/components/SVMessage';
import SVModal, { useModalClose } from '@pkgs/shared-client/components/SVModal';
import SVModalUsersList from '@pkgs/shared-client/components/SVModalUsersList';
import { toast } from '@pkgs/shared-client/components/SVToast';
import SVToggle from '@pkgs/shared-client/components/SVToggle';
import cn from '@pkgs/shared-client/helpers/cn';
import fieldNameFromQuery from '@pkgs/shared-client/helpers/fieldNameFromQuery';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import BoardOwnershipType from '@pkgs/shared/enums/BoardOwnershipType';
import BoardUserRole from '@pkgs/shared/enums/BoardUserRole';
import InviteType from '@pkgs/shared/enums/InviteType';
import boardUserRoleHasBoardUserRolePrivileges from '@pkgs/shared/helpers/boardUserRoleHasBoardUserRolePrivileges';
import { useState } from 'react';

type LikeUser = Parameters<
	NonNullable<React.ComponentProps<typeof SVModalUsersList.SelectItem>['onSelect']>
>[0];

const SVEditBoardModal = SVModal.create(
	({
		board,
		noRedirectOnDelete,
	}: {
		board: BoardFragmentFragment;
		noRedirectOnDelete?: boolean;
	}) => {
		const client = useApolloClient();
		const close = useModalClose();
		const [isPrivateBoard, setIsPrivateBoard] = useState(board.isPrivate);
		const [isHidden, setIsHidden] = useState(board.isHidden);
		const [teamEditorHasPermissions, setTeamEditorHasPermissions] = useState(
			!!board.isTeamEditorAnEditor,
		);
		const [selectedUsers, setSelectedUsers] = useState<LikeUser[]>([]);
		const [inviteSubmit, setInviteSubmit] = useState<(() => Promise<void>) | null>(null);
		const authUser = useAuthUser(['username']);

		const isTeamBoard = board.ownershipType === BoardOwnershipType.TEAM;

		const [name, setName] = useState(board.name);
		const [description, setDescription] = useState(board.description);
		const [isSubmitting, setIsSubmitting] = useState(false);
		const [error, setError] = useState<string | null>(null);

		const handleSubmit = useEventCallback(
			async (values: Omit<UpdateBoardMutationMutationVariables['input'], 'boardID'>) => {
				setIsSubmitting(true);
				setError(null);

				try {
					await client.mutate({
						mutation: updateBoardMutation,
						variables: {
							input: {
								boardID: board._id,
								...(!isTeamBoard && {
									isPrivate: isPrivateBoard,
								}),
								...(!isTeamBoard && {
									isHidden: isHidden,
								}),
								isTeamEditorAnEditor: teamEditorHasPermissions,
								...values,
							},
						},
					});

					toast.success(`${isTeamBoard ? 'Team board' : 'Board'} updated`);

					if (selectedUsers.length > 0 && inviteSubmit) {
						await inviteSubmit();
					}

					const fieldName = fieldNameFromQuery(UserItemsQuery, {
						username: authUser?.username,
					});

					if (fieldName) {
						client.cache.evict({
							id: 'ROOT_QUERY',
							fieldName,
							broadcast: false,
						});
					}
					close();
				} catch (error: any) {
					let errorMessage = 'An error occurred while updating the board.';

					toast.error('An error occurred while updating the board.');

					if (error?.graphQLErrors?.[0]?.message) {
						errorMessage = error.graphQLErrors[0].message;
					} else if (error?.message) {
						errorMessage = error.message.replace(/^GraphQL error: /, '');
					}

					setError(errorMessage);
					console.error('Error updating board:', error);
				} finally {
					setIsSubmitting(false);
				}
			},
		);

		const handleUsersSelected = useEventCallback((users: LikeUser[]) => {
			setSelectedUsers(users);
		});

		const handleInviteSubmit = useEventCallback((submitFn: () => Promise<void>) => {
			setInviteSubmit(() => submitFn);
		});

		const hasAdminPrivileges = boardUserRoleHasBoardUserRolePrivileges(
			board.role,
			BoardUserRole.ADMIN,
		);

		const handleDelete = useEventCallback(() => {
			if (!board) {
				return;
			}

			SVModal.open(SVDeleteBoardModal, {
				_id: board._id,
				name: board.name,
				ownershipType: board.ownershipType,
				noRedirect: noRedirectOnDelete,
				url: board.url,
			});
		});

		return (
			<div className="-sm:w-full flex max-h-[648px] w-[345px] flex-col">
				<SVModal.Title className="mb-2 flex-shrink-0">
					Edit {isTeamBoard ? 'team ' : ''}board
				</SVModal.Title>
				{error && (
					<SVMessage use={SVMessage.USES.ERROR} className="mb-4">
						{error}
					</SVMessage>
				)}
				<div className="overflow-y-auto p-1">
					<div>
						{isTeamBoard ? (
							<SVFormLayout.P>
								Team boards enable collaboration among your entire team.
							</SVFormLayout.P>
						) : null}
						<div className={isTeamBoard ? 'mt-3' : 'mt-2'}>
							<input
								className={
									'type-base bg-background input-text-default w-full rounded-lg border-0 px-4 py-3 placeholder-gray-700 ring-1 ring-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600'
								}
								name="name"
								title="Board name"
								placeholder=""
								required={true}
								value={name || ''}
								onChange={(e) => setName(e.target.value)}
							/>
						</div>
						{!isTeamBoard ? (
							<div className="mb-5 mt-5 flex justify-between">
								<div className="flex flex-col text-start">
									<div className="text-base font-semibold text-gray-300">
										Private Board
									</div>
									<div className="type-label text-muted">
										Only you and collaborators can see it.
									</div>
								</div>
								<SVToggle
									isPressed={isPrivateBoard}
									onClick={() => setIsPrivateBoard(!isPrivateBoard)}
									use="extension-settings"
								/>
							</div>
						) : null}

						{!isTeamBoard ? (
							<div className="mb-5 mt-5 flex justify-between">
								<div className="flex flex-col text-start">
									<div className="text-base font-semibold text-gray-300">
										Hide from feed
									</div>
									<div className="type-label text-muted">
										Won't show up on your profile and feed.
									</div>
								</div>
								<SVToggle
									isPressed={isHidden}
									onClick={() => setIsHidden(!isHidden)}
									use="extension-settings"
								/>
							</div>
						) : null}

						{isTeamBoard ? (
							<div className="mb-5 mt-5 flex items-center justify-between">
								<div className="flex flex-col text-start">
									<div className="text-base font-semibold text-gray-500">
										Team editor can edit
									</div>
									<div className="type-label text-muted pr-2">
										If disabled, only team owners, admins and curators can
										add/remove saves from this board.
									</div>
								</div>
								<SVToggle
									isPressed={teamEditorHasPermissions}
									onClick={() =>
										setTeamEditorHasPermissions(!teamEditorHasPermissions)
									}
									use="extension-settings"
								/>
							</div>
						) : null}

						{!isTeamBoard ? (
							<div className="mb-5 mt-14 flex items-center justify-between">
								<div className="text-base font-semibold text-gray-500">
									Collaborators
								</div>
								<SVEditBoardCollaboratorsActionContainer
									board={board}
									isAdmin={hasAdminPrivileges}
									render={(props) => (
										<div {...props}>
											<SVAvatarsStack
												users={board.collaborators}
												showDropdown={false}
												invites={board.invites}
												size={SVAvatarsStack.SIZES.EXTRA_LARGE}
												showAdd={false}
												visibleCount={3}
												{...props}
											/>
										</div>
									)}
								/>
							</div>
						) : null}
						{!isTeamBoard ? (
							<div className="mt-0.5">
								<SVInviteUsersContainer
									type={isTeamBoard ? InviteType.TEAM : InviteType.BOARD}
									newBoard={false}
									resourceID={board._id}
									onUsersSelected={handleUsersSelected}
									onSubmit={handleInviteSubmit}
								/>
							</div>
						) : null}
					</div>
				</div>
				<div
					className={cn(
						'mt-14 flex w-full flex-row font-normal',
						hasAdminPrivileges ? 'justify-between' : 'justify-end',
					)}
				>
					{hasAdminPrivileges && (
						<button
							className="type-small flex cursor-pointer items-end justify-center whitespace-nowrap text-center align-middle font-medium text-gray-500 underline outline-none transition-colors ease-out hover:text-gray-200"
							onClick={() => handleDelete()}
						>
							Delete
						</button>
					)}
					<SVButton
						onClick={() => handleSubmit({ name, description })}
						use={SVButtonUSES.PRIMARY}
						isLoading={isSubmitting}
						className="type-label type-label rounded-[16px] bg-gray-200 px-5 py-4"
						size={SVButtonSIZES.TINY}
					>
						Save
					</SVButton>
				</div>
			</div>
		);
	},
);

export default SVEditBoardModal;
